/**
 * The frame for sign-in pages: a narrow centred column with the brand, a heading, a line of
 * explanation and the page's controls. Shared by the sign-in, second-factor and confirm pages so
 * every step of signing in looks like the same place.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useRef, useSyncExternalStore } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { focusRing } from "@lattice/ui/focus";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Mark } from "@lattice/ui/Mark";
export const authStyles = {
  page: {
    kAzted: "x1ov3xa9",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kmVPX3: "x1g83aip",
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kMv6JI: "x1cikjda",
    kGuDYH: "x1rf3zrh",
    kLWn49: "xbz0q3o",
    $$css: true
  },
  panel: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x1qjc9v5",
    kOIVth: "x15agyyk",
    kzqmXN: "xh8yej3",
    ks0D6T: "x1ptgtm5",
    $$css: true
  },
  brand: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kSGwAc: "xqcrz7y",
    kAzted: "x1gg8mnh",
    kOIVth: "x1173whf",
    kMwMTN: "x10dbofl",
    kybGjl: "x1hl2dhg",
    k63SB2: "xqu410z",
    kaIpWk: "x8wwnw1",
    $$css: true
  },
  brandWord: {
    kMwMTN: "xzb3lse",
    $$css: true
  },
  words: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x626il7",
    $$css: true
  },
  heading: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x1u29x3t",
    k63SB2: "xqu410z",
    kQqvRs: "x1ntj4s9",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  body: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1jty25b",
    kN2L0X: "x1fzhlzt",
    kHjlTd: "xj0a0fe",
    $$css: true
  },
  form: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1r0x45p",
    $$css: true
  },
  field: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xskm3c9",
    $$css: true
  },
  label: {
    k63SB2: "xqu410z",
    $$css: true
  },
  input: {
    kAzted: "x1wiwyrm",
    kg3NbH: "x179o8vp",
    kaIpWk: "x1m3fsnl",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1gnjola x19omjqh",
    kWkggS: "xcqm4px",
    kMwMTN: "xzb3lse",
    kMv6JI: "x1cikjda",
    kGuDYH: "x1rf3zrh",
    kcqcaj: "xss6m8b",
    kjBf7l: "x2jzzdg",
    kInvED: "x1ewu8gn",
    kMeerF: "x1de99jn",
    k3XXqK: "x1t137rt x9v5kkp",
    $$css: true
  },
  code: {
    ks0D6T: "x1mtlzti",
    $$css: true
  },
  error: {
    kogj98: "x1ghz6dp",
    kGuDYH: "xl4dl4e",
    kMwMTN: "x156x1qg",
    $$css: true
  },
  focusTarget: {
    k3XXqK: "x1t137rt",
    $$css: true
  }
};

/*
  Once a code has been asked for, the page lead saying "we will send a code" is no longer true, so
  the sign-in form tells the layout and the lead moves on to the next step.
*/
let codeAsked = false;
const watchers = new Set<() => void>();
export function setCodeAsked(asked: boolean): void {
  codeAsked = asked;
  for (const notify of watchers) notify();
}
function watchCodeAsked(notify: () => void): () => void {
  watchers.add(notify);
  return () => watchers.delete(notify);
}
export function AuthLayout({
  title,
  lead,
  children,
  focusHeading = false,
  alert = false,
  testId
}: {
  readonly title: string;
  readonly lead: string;
  readonly children: ReactNode;
  /**
   * Moves focus to the heading on mount, for a screen that replaces a form in place. The control
   * that held focus is gone, so without this focus falls to the body and a screen reader says nothing.
   */
  readonly focusHeading?: boolean;
  /** Announces the heading and lead when they replace a loading state with a dead end. */
  readonly alert?: boolean;
  readonly testId?: string;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const asked = useSyncExternalStore(watchCodeAsked, () => codeAsked);
  useEffect(() => {
    if (focusHeading) heading.current?.focus();
  }, [focusHeading]);
  return <main className="x1ov3xa9 xrvj5dj x1ku5rj1 x1g83aip x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o" data-testid={testId}>
      <div className="x78zum5 xdt5ytf x1qjc9v5 x15agyyk xh8yej3 x1ptgtm5">
        <a href="/" {...stylex.props(authStyles.brand, focusRing)} aria-label="Lattice home">
          <Mark size={22} />
          <span className="xzb3lse">Lattice</span>
        </a>
        <div role={alert ? "alert" : undefined} className="x78zum5 xdt5ytf x626il7">
          <h1 ref={heading} tabIndex={focusHeading ? -1 : undefined} className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw x1t137rt">
            {title}
          </h1>
          {/* Once a code is asked for, the form says where it went, so the lead would say it twice. */}
          {asked ? null : <p className="x1ghz6dp x1jty25b x1fzhlzt xj0a0fe">{lead}</p>}
        </div>
        {children}
      </div>
    </main>;
}

/** A path inside this site to return to, from `?next=`, or null when it is missing or points away. */
export function nextPath(): string | null {
  const next = new URLSearchParams(window.location.search).get("next");
  return next !== null && next.startsWith("/") && !next.startsWith("//") ? next : null;
}

/** The sign-in page for whoever was headed to `next`: staff to the admin, clinic members to the clinic. */
export function signInAgainPath(): string {
  const next = nextPath();
  const query = new URLSearchParams();
  if (next?.startsWith("/admin") === true) query.set("for", "staff");else if (next?.startsWith("/clinic") === true) query.set("for", "clinic");
  if (next !== null) query.set("next", next);
  const search = query.toString();
  return search === "" ? "/sign-in" : `/sign-in?${search}`;
}

/** The way out of a page that could not load: ask again, or write to support. */
export function TryAgain() {
  return <div className="x78zum5 xdt5ytf x1r0x45p">
      <Button onClick={() => window.location.reload()}>Try again</Button>
      <LinkButton href="/support" tone="quiet">
        Contact support
      </LinkButton>
    </div>;
}